/**
 * The dialog, on the native `<dialog>` element.
 *
 * Five idioms had grown up for this: a portalled `dialog.modal-open` copied
 * into three files, a react-daisyui `Modal` in two more, and ten
 * `window.confirm` calls behind an eslint disable. They disagreed on focus,
 * on Escape, and on whether the backdrop closed anything.
 *
 * `showModal()` is what gives the focus trap, Escape, the top layer and the
 * inert background, so this drives the element rather than reimplementing it.
 *
 * @module components/ui/Modal
 */

import React, { type ReactNode, useCallback, useEffect, useId, useRef } from "react";

import { IconButton } from "./Button";
import { EditLockExempt } from "./EditLock";
import { Icon } from "./Icon";

export interface ModalProps {
  open: boolean;
  /** Called for every close path: the button, Escape, the backdrop. */
  onClose: () => void;
  title: ReactNode;
  /** Sits under the title, for the object the dialog acts on. */
  subtitle?: ReactNode;
  children: ReactNode;
  /** The action row. Least destructive first, so Enter does not confirm a delete. */
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  /** `danger` for a decision that loses something: an alert, not a form. */
  tone?: "default" | "danger";
  /** Announce the body as the description: a question whose body is its consequence. */
  describe?: boolean;
  /**
   * Why the dialog cannot close yet, such as an action in flight. Holds the
   * close button, Escape and the backdrop, and names the reason on the button.
   */
  closeRefusal?: string;
  "data-testid"?: string;
}

/**
 * The unsaved-work guard, whose two halves are only meaningful together.
 *
 * Declaring `dirty` alone type-checks and renders, and silently discards the
 * edit it reads as protecting, so the type requires the handler alongside it.
 */
export type ModalGuard =
  | { dirty?: false; onConfirmDiscard?: never }
  | { dirty: true; onConfirmDiscard: () => Promise<boolean> };

const SIZE_CLASS: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "max-w-[400px]",
  md: "max-w-[560px]",
  lg: "max-w-[720px]",
};

/**
 * @example
 * ```tsx
 * <Modal open={open} onClose={close} title="Record a finding"
 *   footer={<><Button onClick={close}>Cancel</Button><Button variant="primary">Record</Button></>}>
 *   …
 * </Modal>
 * ```
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle = undefined,
  children,
  footer = undefined,
  size = "md",
  tone = "default",
  describe = tone === "danger",
  closeRefusal = undefined,
  dirty = false,
  onConfirmDiscard = undefined,
  "data-testid": testId,
}: ModalProps & ModalGuard): React.ReactElement {
  const ref = useRef<HTMLDialogElement>(null);
  const headingId = useId();
  const bodyId = useId();
  // A question is announced with its description, which is where the
  // consequence of the decision lives.
  const describedBy = describe && children != null ? bodyId : undefined;
  // Where focus came from. The browser restores it for a dialog it opened
  // itself, but not reliably when React unmounts the trigger underneath.
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) {
      return undefined;
    }
    if (open && !dialog.open) {
      opener.current = document.activeElement;
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
      if (opener.current instanceof HTMLElement) {
        opener.current.focus();
      }
      // Cleared once used: the unmount cleanup below reads the same ref, and a
      // stale opener would pull focus out of whatever the user moved on to
      // long after this dialog closed.
      opener.current = null;
    }
    return undefined;
  }, [open]);

  // Unmounting while open never reaches the close path above, so the browser
  // drops the dialog and focus falls to the body. Restoring it here keeps a
  // keyboard user where they were when a route change takes the modal away.
  useEffect(
    () => () => {
      if (opener.current instanceof HTMLElement && opener.current.isConnected) {
        opener.current.focus();
      }
    },
    [],
  );

  // `cancel` fires for Escape. Preventing it is the only way to hold a dialog
  // open long enough to ask whether unsaved work should be discarded.
  const requestClose = useCallback(
    (event?: { preventDefault: () => void }) => {
      event?.preventDefault();
      if (closeRefusal !== undefined) {
        return;
      }
      if (!dirty || !onConfirmDiscard) {
        onClose();
        return;
      }
      onConfirmDiscard()
        .then((discard) => {
          if (discard) {
            onClose();
          }
        })
        .catch(() => undefined);
    },
    [closeRefusal, dirty, onConfirmDiscard, onClose],
  );

  // The dialog's own cancel event, which Escape fires. React carries a nested
  // dialog's cancel and close up the component tree, so only our own count.
  const handleCancel = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      if (event.target !== event.currentTarget) {
        return;
      }
      requestClose(event);
    },
    [requestClose],
  );

  // A close the browser forced past an uncancellable second Escape. A guarded
  // dialog reopens and asks again; an unguarded one tells the parent, which
  // still holds it open. Our own close arrives with `open` already false.
  const handleClose = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      const dialog = ref.current;
      if (!open || !dialog || event.target !== dialog) {
        return;
      }
      if (closeRefusal !== undefined || (dirty && onConfirmDiscard)) {
        dialog.showModal();
      }
      requestClose();
    },
    [open, closeRefusal, dirty, onConfirmDiscard, requestClose],
  );

  // A pointer close, whose event type is unrelated to the dialog's.
  const handleDismiss = useCallback(() => {
    requestClose();
  }, [requestClose]);

  return (
    <dialog
      ref={ref}
      className="modal"
      role={tone === "danger" ? "alertdialog" : undefined}
      aria-labelledby={headingId}
      aria-describedby={describedBy}
      onCancel={handleCancel}
      onClose={handleClose}
      data-testid={testId}
    >
      <div className={`modal-box overflow-hidden p-0 shadow-none ${SIZE_CLASS[size]}`}>
        <div
          className={`flex items-start justify-between gap-4 border-b border-base-300 px-6 py-5 ${tone === "danger" ? "bg-error-bg" : "bg-base-200"}`}
        >
          <div className="min-w-0">
            <h2
              id={headingId}
              className="flex items-center gap-2 text-lg font-bold tracking-[-0.01em]"
            >
              {tone === "danger" && (
                <Icon name="warning" className="text-error-strong" aria-hidden />
              )}
              {title}
            </h2>
            {subtitle && <p className="mt-0.5 text-md text-muted">{subtitle}</p>}
          </div>
          <EditLockExempt>
            <IconButton
              icon="x"
              aria-label="Close"
              size="sm"
              disabled={closeRefusal !== undefined}
              disabledReason={closeRefusal}
              // Leftwards: the box clips its overflow above this top-corner button.
              disabledReasonPlacement="left"
              onClick={handleDismiss}
              data-testid={testId ? `${testId}-close` : undefined}
            />
          </EditLockExempt>
        </div>
        <div id={bodyId} className="max-h-[70vh] overflow-y-auto p-6">
          {children}
        </div>
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-base-300 bg-base-200 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
      {/* Pointer-only, and hidden from assistive technology: it duplicates the
          header's close button, and two controls with one name is worse than
          one. Escape already routes through onCancel above. */}
      <button
        type="button"
        className="modal-backdrop"
        aria-hidden="true"
        tabIndex={-1}
        onClick={handleDismiss}
      />
    </dialog>
  );
}
